/* eslint-disable react/jsx-no-target-blank */

import { useRef, useState } from 'react'
import Swal from 'sweetalert2'

import { AtendimentoEsusContexto, AtendimentoView } from '../../../atendimento/core/_models'
import {
  AnotacaoType,
  AtendimentoEstado,
  AtendimentoType,
  IconPaths,
  KTSVG,
  ModalSize,
  showAlert,
  showAlertLoading,
  showConfirmAlert,
  showError,
} from '../../../../../../_metronic/helpers'
import {
  alteraAtendimento,
  generateAso,
  getAtendimentoById,
  getContextoEsusEncerramento,
} from '../../../atendimento/core/_requests'
import { useAuth } from '../../../../auth'
import { temVinculoMedico } from 'app/modules/auth/core/vinculos'
import { useNavigate } from 'react-router-dom'
import { AnamneseModal } from '../modal/AnamneseModal'
import { EncerramentoEsusModal, EncerramentoEsusValores } from '../modal/EncerramentoEsusModal'
import { Button, LoadingIndicator, RailItem, useModal } from '../../../../../components'
import {
  AnotacaoModal,
  DiagnosticoIAModal,
  MarcarFaltaModal,
  ResultadoIAModal,
  ResultadoIAModalRef,
  ResultJSON,
} from '../../atendimentos-medico-list/component/modal'
import { DispositivosModal, CIDModal, InterrupcaoAtendimentoModal } from '../modal'
import { FormularioModal } from '../modal/formulario-answer/FormularioModal'
import { ExamesModal } from '../../../atendimento-ASO/components/modal'
import { Aso } from '../../../atendimento-ASO/aso-generate-list/components/modal/Aso'
import { signAso } from '../../../atendimento-ASO/aso-sign-list/core/_request'
import { DocumentoModal, DocumentoModalRef } from '../../prescricao/components/modal/DocumentoModal'
import { useCertificado } from '../../../../../helpers/hooks/useCertificado'
import {
  FormularioAtendimentoDtoAssinatura,
  FormularioAtendimentoList,
} from '../../../avaliacoes/avaliacoes-pendentes-list/core/_models'
import { signAvaliacao } from '../../../avaliacoes/avaliacoes-pendentes-list/core/_requests'
import {
  formulariosParaResponder,
  SelecionarFormularioModal,
} from '../modal/SelecionarFormularioModal'
import {
  getFormularioAtendimento,
  getFormularioResolvido,
} from '../../../avaliacoes/avaliacoes-list/core/_requests'
import { useT } from '_metronic/i18n/Metronici18n'
import styles from './styles/aside-atendimento.module.css'
import { TriagemCard } from '../components/triagem/TriagemCard'
import { ExamesImagem } from '../components/exames-imagem/ExamesImagem'
import { exameImagemHabilitado } from '../components/exames-imagem/exameImagemFlag'
import { useChamadaAtiva } from '../../chamada-ativa/ChamadaAtivaProvider'

type Props = {
  atendimento: AtendimentoView
  onFinalizar?: () => Promise<boolean | null>
  temAlteracoesNaoSalvas?: boolean
}
export const AsideMenuAtendimentoMain: React.FC<Props> = ({
  atendimento,
  onFinalizar,
  temAlteracoesNaoSalvas,
}) => {
  const { showModal } = useModal()
  const { ensurePassword } = useCertificado()
  const { t } = useT()

  const resultadoIARef = useRef<ResultadoIAModalRef>(null)
  const documentoModalRef = useRef<DocumentoModalRef>(null)

  const { currentUser } = useAuth()

  const navigate = useNavigate()
  const { encerrarChamada } = useChamadaAtiva()
  const isMedico = temVinculoMedico(currentUser?.vinculos)

  const [answered, setOnAnswered] = useState(false)
  const [jsonData, setJsonData] = useState<ResultJSON | undefined>()
  const [prompt, setPrompt] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  const handleAnswered = () => setOnAnswered(true)

  const pacienteId = atendimento.paciente?.id || ''

  const temAnamnese = atendimento.anotacoes?.some(
    (item) => item.tipo === AnotacaoType.Anamnese && item.anotacao?.trim()
  )
  const qtdExames = atendimento.procedimentos?.length ?? 0
  const temFormularios = !!atendimento.formularios && atendimento.formularios.length > 0

  const handleShowExame = () =>
    showModal({
      title: tt('exames'),
      content: <ExamesModal isAtendimento atendimentoId={atendimento.id} />,
      size: ModalSize.Large,
    })

  const showExamesImagem = () =>
    showModal({
      title: t('exames_imagem.titulo'),
      content: <ExamesImagem idAtendimento={Number(atendimento.id)} />,
      size: ModalSize.ExtraLarge,
    })

  const showInformacoesDispositivo = () =>
    showModal({
      title: tt('dispositivos_informacoes'),
      content: <DispositivosModal />,
      size: ModalSize.ExtraLarge,
      fetchers: {
        atendimento: () => getAtendimentoById(atendimento.id),
      },
    })

  const showCID = () =>
    showModal({
      title: tt('cid'),
      content: <CIDModal />,
      size: ModalSize.Medium,
      fetchers: {
        atendimento: () => getAtendimentoById(atendimento.id),
      },
    })

  const showAnamnese = () =>
    showModal({
      title: tt('anamnese'),
      content: <AnamneseModal />,
      fetchers: {
        atendimento: () => getAtendimentoById(atendimento.id),
      },
    })

  const showAso = () =>
    showModal({
      title: tt('aso'),
      content: <Aso id={atendimento?.id} />,
      size: ModalSize.ExtraLarge,
    })

  const showFormularioResposta = () => {
    const filteredFormularios = formulariosParaResponder(atendimento.formularios)

    if (filteredFormularios.length === 0) return

    if (filteredFormularios.length === 1) {
      openAvaliacao(filteredFormularios[0])
      return
    }

    showModal({
      title: tt('selecionar_avaliacao'),
      content: (
        <SelecionarFormularioModal
          formularios={filteredFormularios}
          onSelect={(formulario) => {
            openAvaliacao(formulario)
          }}
        />
      ),
    })
  }

  const openAvaliacao = (avaliacao: FormularioAtendimentoList) => {
    const { nome } = avaliacao.formulario

    showModal({
      title: nome,
      content: <FormularioModal atendimentoId={Number(atendimento.id)} onSubmit={handleAnswered} />,
      alignLeft: true,
      disableClose: true,
      fetchers: {
        formularioAtendimento: () => getFormularioAtendimento(atendimento.id, avaliacao.id),
        formularioResolvido: () => getFormularioResolvido(atendimento.id, avaliacao.id),
      },
    })
  }

  const handlePrescricaoLauduz = () =>
    window.open(
      `/apps/atendimentosMedicos/prescricao/lauduz/${atendimento.id}?paciente=${pacienteId}`,
      '_blank',
      'noreferrer'
    )

  const handlePrescricaoMemed = () =>
    window.open(
      `/apps/atendimentosMedicos/prescricao/memed/${atendimento.id}?paciente=${pacienteId}`,
      '_blank',
      'noreferrer'
    )

  const showFaltouAtendimento = () =>
    showModal({
      title: tt('paciente_ausente.title.modal', { name: atendimento.paciente?.pessoa?.nome }),
      content: (
        <MarcarFaltaModal
          atendimentoId={Number(atendimento.id)}
          onAtendimentoEncerrado={() => encerrarChamada(Number(atendimento.id))}
        />
      ),
      size: ModalSize.Medium,
    })

  /**
   * Lê o contexto e-SUS do atendimento (E1d) para decidir se o bloco de encerramento aparece.
   *
   * Qualquer falha — 403, 404, rede, timeout — resolve como integração desligada: o bloco e-SUS
   * jamais pode impedir alguém de encerrar um atendimento, então a degradação é silenciosa e o
   * fluxo segue idêntico ao de quem não usa a integração.
   */
  const carregarContextoEsus = async (): Promise<AtendimentoEsusContexto> => {
    try {
      const contexto = await getContextoEsusEncerramento(atendimento.id)
      return contexto ?? { esusAtivo: false, lotacoes: [], condutas: [] }
    } catch {
      return { esusAtivo: false, lotacoes: [], condutas: [] }
    }
  }

  /**
   * Abre o bloco e-SUS e devolve o que o profissional confirmou, ou `null` se ele fechou/cancelou
   * o modal — nesse caso nada é gravado e ele continua na tela, com o botão "Finalizar" disponível.
   */
  const coletarDadosEsus = (contexto: AtendimentoEsusContexto) =>
    new Promise<EncerramentoEsusValores | null>((resolve) => {
      showModal({
        title: 'Informações para o e-SUS',
        content: (
          <EncerramentoEsusModal
            lotacoes={contexto.lotacoes}
            condutas={contexto.condutas}
            // Backend mais antigo não manda a chave: `?? []` some com o bloco em vez de quebrar.
            procedimentos={contexto.procedimentos ?? []}
            onConfirm={(valores) => resolve(valores)}
          />
        ),
        size: ModalSize.Medium,
        onClose: () => resolve(null),
      })
    })

  /**
   * Declaração de interrupção da teleconsulta — entrada própria, fora do caminho
   * de `finalizarAtendimento`. É opcional por desenho: nada aqui pode virar
   * passo, pergunta ou confirmação extra no encerramento.
   */
  const showInterrupcaoAtendimento = () =>
    showModal({
      title: 'Registrar interrupção da teleconsulta',
      content: <InterrupcaoAtendimentoModal atendimentoId={Number(atendimento.id)} />,
    })

  const formulariosParaAssinar = (atend?: AtendimentoView) =>
    (atend?.formularios ?? []).filter(
      (f) => f.formulario?.assinar && f.respondido && !f.concluido && !f.cancelado
    )

  /** Pede o certificado e assina os formulários; `false` se o profissional desistiu no caminho. */
  const assinarFormularios = async (formularios: FormularioAtendimentoList[]) => {
    showAlertLoading()

    const { ok, tipo } = await ensurePassword()
    if (!ok || !tipo) return false

    await Promise.all(
      formularios.map((item) => {
        const formularioAt: FormularioAtendimentoDtoAssinatura = {
          atendimento: Number(atendimento.id),
          formularioAtendimento: Number(item.id),
          tipo,
        }

        return signAvaliacao(formularioAt)
      })
    )
    return true
  }

  const finalizarAtendimento = async () => {
    const atend = await getAtendimentoById(atendimento.id)

    const shouldContinue = await onFinalizar?.()
    if (!shouldContinue) return

    // Só o caminho novo do bloco e-SUS pode segurar o usuário na tela; sem integração ativa a
    // navegação do `finally` continua exatamente como era.
    let manterNaTela = false
    let contextoEsus: AtendimentoEsusContexto = { esusAtivo: false, lotacoes: [], condutas: [] }
    let atendimentoEncerrado = false

    showAlertLoading()
    let finalizado = false
    try {
      if (atendimento.tipo !== AtendimentoType.Ocupacional) {
        const hasntRetorno =
          atend?.prescricoes?.some((prescricao) => prescricao.atestado?.tipo?.retorno) || false

        if (!hasntRetorno) {
          const wantRetorno = await showConfirmAlert({
            title: 'Você gostaria de criar um encaminhamento de retorno?',
            text: 'Isto é opcional.',
            confirmButtonText: 'Sim',
            icon: 'info',
          })
          if (wantRetorno)
            await new Promise<void>((resolve) => {
              showModal({
                title: 'Adicionar Documento',
                content: (
                  <DocumentoModal
                    ref={documentoModalRef}
                    onConfirm={() => resolve()}
                    onlyRetorno={true}
                  />
                ),
                fetchers: {
                  atendimento: () => getAtendimentoById(Number(atendimento.id)),
                },
                size: ModalSize.Medium,
                onClose: () => {
                  documentoModalRef.current?.onClose()
                  resolve()
                },
              })
            })
        }
        showAlertLoading()
      }

      contextoEsus = await carregarContextoEsus()

      let lotacaoEsus: number | undefined
      let condutasEsus: number[] | undefined

      if (contextoEsus.esusAtivo) {
        // O alerta de carregamento fica por cima do modal — fecha antes de abrir o bloco e volta
        // logo depois, para o PUT sair com o mesmo feedback visual de sempre.
        Swal.close()
        const dadosEsus = await coletarDadosEsus(contextoEsus)
        if (!dadosEsus) {
          manterNaTela = true
          return
        }
        lotacaoEsus = dadosEsus.lotacao
        condutasEsus = dadosEsus.condutas
        showAlertLoading()
      }

      await alteraAtendimento(
        Number(atendimento.id),
        AtendimentoEstado.Finalizado,
        Number(currentUser?.pessoa?.id),
        true,
        null,
        lotacaoEsus,
        condutasEsus
      )
      atendimentoEncerrado = true
      finalizado = true
      if (atendimento.tipo === AtendimentoType.Ocupacional) {
        const wantToSign = await showConfirmAlert({
          title: 'Você gostaria de assinar o ASO?',
          text: 'Isto é opcional.',
          confirmButtonText: 'Sim',
          icon: 'info',
        })
        if (wantToSign) {
          showAlertLoading()
          await generateAso(Number(atendimento.id))
          const { ok, tipo } = await ensurePassword()
          if (!ok || !tipo) return
          await signAso(Number(atendimento.id), tipo)
        }
      }
      if (answered && atendimento.formularios && atendimento.formularios.length > 0) {
        const paraAssinar = formulariosParaAssinar(atend)
        if (paraAssinar.length > 0) {
          const wantToSign = await showConfirmAlert({
            title: 'Você gostaria de assinar as avaliações?',
            text: 'Isto é opcional.',
            confirmButtonText: 'Sim',
            icon: 'info',
          })
          if (wantToSign && !(await assinarFormularios(paraAssinar))) return
        }
      }

      await showAlert({ title: 'Sucesso!', text: 'Atendimento Finalizado', icon: 'success' })
    } catch (error) {
      // Erro de negócio do bloco e-SUS (422 de lotação/conduta) não encerra o atendimento: manter
      // o usuário na tela é o que permite corrigir e tentar de novo, em vez de sair sem encerrar.
      if (contextoEsus.esusAtivo && !atendimentoEncerrado) manterNaTela = true
      showError(error)
    } finally {
      // Mesmo instante em que a chamada caía antes (LO-829): só se o atendimento finalizou.
      if (!manterNaTela && finalizado) {
        encerrarChamada(Number(atendimento.id))
        navigate('/apps/atendimentosMedicos/lista')
      }
    }
  }

  const onExit = async () => {
    if (temAlteracoesNaoSalvas) {
      const isConfirmed = await showConfirmAlert({
        title: 'Sair sem salvar?',
        text: 'Há alterações na anamnese que ainda não foram salvas. Deseja sair mesmo assim?',
        icon: 'warning',
        confirmButtonText: 'Sair',
      })
      if (!isConfirmed) return
    }
    navigate('/apps/atendimentosMedicos/lista')
  }

  const showAnotacaoModal = () =>
    showModal({
      title: tt('pendencia'),
      content: <AnotacaoModal atendimentoId={atendimento.id} />,
    })

  const handleIA = (resultJson: ResultJSON, prompt: string) => {
    setJsonData(resultJson)
    setPrompt(prompt)
  }

  const showIaDiagnostics = () => {
    if (atendimento.formularios && atendimento.formularios.length > 0) {
      const id = atendimento.formularios[0].id
      showModal({
        title: tt('gerar_IA'),
        content: <DiagnosticoIAModal onClose={handleIA} setIALoading={setIsLoading} />,
        fetchers: {
          atendimento: () => getAtendimentoById(atendimento.id),
          formularioAtendimento: () => getFormularioAtendimento(atendimento.id, id),
        },
      })
    } else {
      showModal({
        title: tt('gerar_IA'),
        content: <DiagnosticoIAModal onClose={handleIA} setIALoading={setIsLoading} />,
        fetchers: {
          atendimento: () => getAtendimentoById(atendimento.id),
        },
      })
    }
  }

  const showIaResults = () => {
    showModal({
      title: tt('resultado_IA'),
      content: (
        <ResultadoIAModal
          resultData={jsonData as ResultJSON}
          prompt={prompt}
          onChangeResultData={setJsonData}
          ref={resultadoIARef}
        />
      ),
      size: ModalSize.ExtraLarge,
      onClose: () => resultadoIARef.current?.onClose(),
    })
  }

  const tt = (key: string, values?: any) => t(`atendimento.${key}`, undefined, values)

  const exameTipo = atendimento.tipo === AtendimentoType.Ocupacional
  const exameLabel = exameTipo ? tt('aso') : tt('exames')
  const exameSub = exameTipo
    ? 'Documento ASO'
    : qtdExames > 0
    ? `${qtdExames} ${qtdExames === 1 ? 'anexo' : 'anexos'}`
    : 'Sem anexos'

  return (
    <div className='d-flex flex-column h-100 gap-3'>
      {/* Assistente IA + Finalizar (topo, ao lado do cabeçalho) */}
      <div className='d-flex flex-column gap-2'>
        <div className={styles.aiContainer}>
          <div
            className={`d-flex align-items-center gap-1 mb-2 fs-9 fw-bold text-uppercase ${styles.textEmphasis}`}
          >
            <KTSVG path={IconPaths.Magic} className='svg-icon-em me-1' />
            Assistente IA Lauduz
          </div>
          <div className='d-flex flex-column gap-2'>
            <Button className={styles.brandAI} onClick={showIaDiagnostics}>
              {isLoading ? (
                <LoadingIndicator />
              ) : (
                <>
                  <KTSVG path={IconPaths.Bot} className='svg-icon-em text-white me-2' />
                  {tt('gerar_IA')}
                </>
              )}
            </Button>
            {jsonData && (
              <Button className='btn-light-primary' size='btn-sm' onClick={showIaResults}>
                <KTSVG path={IconPaths.File} className='svg-icon-em me-2' />
                {tt('resultado_IA')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Triagem: primeiro item da avaliação, visível sem rolagem */}
      <TriagemCard
        idAtendimento={Number(atendimento.id)}
        idEmpresa={atendimento.empresa?.id ?? atendimento.paciente?.empresa?.id}
        isMedico={isMedico}
      />

      {/* Avaliação */}
      <div className='card border shadow-sm'>
        <div className='card-body p-3'>
          <div className='mb-2 fs-9 fw-bold text-uppercase text-gray-700'>Avaliação</div>
          <RailItem
            icon={IconPaths.Notas}
            title={tt('anamnese')}
            sub={temAnamnese ? 'Registrada' : 'Nenhum registro'}
            onClick={showAnamnese}
            rightLabel='Abrir'
          />
          <RailItem
            icon={IconPaths.Pdf}
            title={exameLabel}
            sub={exameSub}
            onClick={exameTipo ? showAso : handleShowExame}
            rightLabel='Ver'
          />
          {exameImagemHabilitado() && (
            <RailItem
              icon={IconPaths.Imagem}
              title={t('exames_imagem.titulo')}
              sub='Solicitar e visualizar'
              onClick={showExamesImagem}
              rightLabel='Abrir'
            />
          )}
          {temFormularios && (
            <RailItem
              icon={IconPaths.NewFile}
              title={tt('preencher_avaliacao')}
              sub={answered ? 'Responder novamente' : 'Preencher avaliação'}
              onClick={showFormularioResposta}
            />
          )}
        </div>
      </div>

      {/* Conduta */}
      <div className='card border shadow-sm'>
        <div className='card-body p-3'>
          <div className='mb-2 fs-9 fw-bold text-uppercase text-gray-700'>Conduta</div>
          <RailItem
            icon={IconPaths.Info}
            title={tt('cid')}
            sub='Adicionar diagnóstico'
            onClick={showCID}
          />
          <div className={styles.railRow}>
            <span className={styles.railIcon}>
              <KTSVG path={IconPaths.Pill} />
            </span>
            <span className={styles.railTexts}>
              <span
                className='text-truncate fs-7 fw-semibold lh-sm text-gray-900'
                title='Prescrição'
              >
                Prescrição
              </span>
            </span>
            {/* Botões do sistema: o btn-light-primary sem borda some sobre o card do trilho. */}
            <span className='d-flex gap-2 flex-shrink-0'>
              <Button
                variant='secondary-outline'
                size='btn-sm'
                onClick={handlePrescricaoLauduz}
                ariaLabel='Emitir receita pela Lauduz'
              >
                Lauduz
              </Button>
              <Button
                variant='secondary-outline'
                size='btn-sm'
                onClick={handlePrescricaoMemed}
                ariaLabel='Emitir receita pela Memed'
              >
                Memed
              </Button>
            </span>
          </div>
        </div>
      </div>

      {/* Aferições */}
      <div className='card border shadow-sm'>
        <div className='card-body p-3'>
          <div className='mb-2 fs-9 fw-bold text-uppercase text-gray-700'>Aferições</div>
          <RailItem
            icon={IconPaths.Maleta}
            title='Aferições'
            sub='Aferições obtidas com o Telekit®'
            onClick={showInformacoesDispositivo}
            rightLabel='Visualizar'
          />
        </div>
      </div>

      {/* Ações finais */}
      <div className='d-flex flex-column gap-3 mt-auto pt-3 border-top'>
        {/* Ações secundárias lado a lado */}
        <div className='d-flex gap-2'>
          <Button
            className='btn-secondary flex-fill fs-8'
            icon={IconPaths.Add}
            onClick={showAnotacaoModal}
          >
            {tt('pendencia_curto')}
          </Button>
          {isMedico && (
            <Button
              className={`${styles.btnWarning} flex-fill fs-8`}
              icon={IconPaths.X}
              onClick={showFaltouAtendimento}
            >
              {tt('paciente_ausente')}
            </Button>
          )}
        </div>

        <Button
          className='btn-light-primary fs-8'
          icon={IconPaths.Info}
          onClick={showInterrupcaoAtendimento}
        >
          Registrar interrupção
        </Button>

        {/* Ação primária dominante */}
        {isMedico && (
          <Button
            className='btn-primary w-100'
            size='btn-lg'
            icon={IconPaths.Check}
            onClick={finalizarAtendimento}
          >
            {t('common.finalize')}
          </Button>
        )}

        {/* Sair — discreto e afastado das demais ações */}
        <div className='separator my-1' />
        <Button
          className='btn-link btn-active-light-danger text-danger'
          icon={IconPaths.Logout}
          onClick={onExit}
        >
          {t('common.exit')}
        </Button>
      </div>
    </div>
  )
}
